/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { Box, Chip, Paper, Skeleton } from '@mui/material';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { Download, ShareButton } from 'components/Download';

Chart.register(...registerables);

/**
 * 📊 Muestra el consumo diario acumulado en un gráfico de barras.
 *
 * @param {object} props - Las propiedades del componente.
 * @param {number} props.currentMonth - El mes actual.
 * @param {number} props.year - El año actual.
 * @param {number} props.rateHorary - La tarifa horaria.
 * @param {array} props.generalReport - El reporte general.
 * @returns {JSX.Element} Un componente que muestra el consumo diario.
 */

export default function DailyConsume(props) {
  const intl = useIntl();
  const { currentMonth, year, rateHorary, generalReport } = props;
  const chartRef = useRef();

  // 📅 Estado para los días del mes
  const [days, setDays] = useState([]);
  // 💰 Estado para los totales
  const [totals, setTotals] = useState({});
  const localeNav = useSelector((state) => state.prefs.locale);

  useEffect(() => {
    // 🔢 Calcula el número de días en el mes
    const daysInMonth = year
      ? moment(`${year}${currentMonth}`).daysInMonth()
      : moment().daysInMonth();
    const daysArray = [];
    // 🌍 Configura la localización
    moment.locale(localeNav);
    // 🔄 Itera sobre los días del mes
    for (let i = 1; i <= daysInMonth; i++) {
      const date = moment({
        year: year || moment().year(),
        month: currentMonth ? currentMonth - 1 : moment().month(),
        day: i,
      });
      daysArray.push({
        day: i,
        weekday: date.format('ddd'),
      });
    }
    // 📥 Actualiza el estado de los días
    setDays(daysArray);
  }, [localeNav, currentMonth, year]);

  // 🏷️ Etiquetas para el gráfico
  const labels = days.map(
    (day) =>
      `${day.weekday.charAt(0).toUpperCase() + day.weekday.slice(1, 3)} ${day.day}`
  );

  // 🎨 Opciones de datos para el gráfico
  const dataOptions =
    rateHorary === 1
      ? [
          {
            label: `${intl.formatMessage({ id: 'app.charge.board.dailyAcumConsump.dataSet.base' })} kWh`,
            backgroundColor: '#bfe5bf',
            data: [],
            type: 'bar',
          },
          {
            label: `${intl.formatMessage({ id: 'app.charge.board.dailyAcumConsump.dataSet.interm' })} kWh`,
            backgroundColor: '#f2e5bf',
            data: [],
            type: 'bar',
          },
          {
            label: `${intl.formatMessage({ id: 'app.charge.board.dailyAcumConsump.dataSet.tip' })} kWh`,
            backgroundColor: '#f2bfbf',
            data: [],
            type: 'bar',
          },
        ]
      : [
          {
            label: `${intl.formatMessage({ id: 'app.charge.board.dailyAcumConsump.label' })}`,
            backgroundColor: '#bfe5bf',
            data: [],
            type: 'bar',
          },
        ];

  // 📊 Datos para el gráfico
  const data = {
    labels,
    datasets: dataOptions,
  };

  // ⚙️ Opciones del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        // 🖱️ Evento click en la leyenda
        onClick: function (e, legendItem) {
          const index = legendItem.datasetIndex;
          const ci = this.chart;
          const meta = ci.getDatasetMeta(index);

          // Toggle the visibility
          meta.hidden =
            meta.hidden === null ? !ci.data.datasets[index].hidden : null;

          // Update totals
          const newTotals = { base: 0, intermediate: 0, punta: 0 };
          ci.data.datasets.forEach((dataset, i) => {
            if (!ci.getDatasetMeta(i).hidden) {
              dataset.data.forEach((value) => {
                if (i === 0) newTotals.base += parseFloat(value);
                if (i === 1) newTotals.intermediate += parseFloat(value);
                if (i === 2) newTotals.punta += parseFloat(value);
              });
            }
          });

          setTotals(newTotals);
          ci.update();
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
      },
    },
  };

  // 🔄 Procesa los datos del reporte general
  const arrayData = [];

  if (generalReport) {
    Object.values(generalReport).forEach((element) => {
      const object = {
        base: 0,
        intermediate: 0,
        punta: 0,
      };

      const reducer = (accumulator, currentValue) => {
        if (currentValue.tariff === 'BASE')
          return {
            base: accumulator.base + parseFloat(currentValue.Kwh),
            intermediate: accumulator.intermediate,
            punta: accumulator.punta,
          };

        if (currentValue.tariff === 'INTERMEDIO')
          return {
            base: accumulator.base,
            intermediate:
              accumulator.intermediate + parseFloat(currentValue.Kwh),
            punta: accumulator.punta,
          };

        return {
          base: accumulator.base,
          intermediate: accumulator.intermediate,
          punta: accumulator.punta + parseFloat(currentValue.Kwh),
        };
      };

      arrayData.push({
        data: Object.values(element.reduce(reducer, object)),
      });
    });
  }

  let trendData = [];

  const currentDay = year
    ? moment(`${year}${currentMonth}`).daysInMonth()
    : moment().daysInMonth();

  arrayData.forEach((element, index) => {
    const baseValue = totals.base !== 0 ? parseFloat(element.data[0]) : 0;
    const intermediateValue =
      totals.intermediate !== 0 ? parseFloat(element.data[1]) : 0;
    const puntaValue = totals.punta !== 0 ? parseFloat(element.data[2]) : 0;

    const total = baseValue + intermediateValue + puntaValue;
    if (rateHorary === 1) {
      data.datasets[0].data.push(element.data[0].toFixed(3));
      data.datasets[1].data.push(element.data[1].toFixed(3));
      data.datasets[2].data.push(element.data[2].toFixed(3));
      if (currentDay === 1 || index < currentDay - 1) {
        if (total > 0) {
          trendData.push([/*index,*/ total]);
        }
      }
    } else {
      if (currentDay === 1 || index < currentDay - 1) {
        if (total > 0) {
          trendData.push([total]);
          data.datasets[0].data.push(total);
        }
      }
    }
  });

  return (
    <Paper
      sx={{ width: '100%', p: 2, overflowX: 'hidden', overflowY: 'auto' }}
      ref={chartRef}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* 🏷️ Título del gráfico */}
        <Chip
          label={
            rateHorary === 1
              ? intl.formatMessage({
                  id: 'app.charge.board.dailyAcumConsump.title',
                })
              : intl.formatMessage({
                  id: 'app.chargeSolar.board.generationPerRate.title',
                })
          }
          sx={{
            fontWeight: 700,
            backgroundColor: '#730065',
            fontSize: '16px',
            color: 'white',
          }}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Download
            itemRef={chartRef}
            name={intl.formatMessage({
              id: 'app.charge.board.dailyAcumConsump.title',
            })}
          />
          <ShareButton
            itemRef={chartRef}
            name={intl.formatMessage({
              id: 'app.charge.board.dailyAcumConsump.title',
            })}
          />
        </Box>
      </Box>
      {/* 📊 Gráfico de barras */}
      <Box style={{ height: '400px' }}>
        {generalReport ? (
          <Bar data={data} options={options} width="100%" height="400px" />
        ) : (
          <Skeleton variant="rectangular" width="100%" height="400px" />
        )}
      </Box>
    </Paper>
  );
}
